import { Box, Container, IconButton, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { replacePathParams } from 'utils/routes.utils';
import { appendLoyaltyIdAndLocaleToExternalTicketUrl } from 'utils/ticket.utils';

import { Routes } from 'constants/routes.constants';
import { useLocale } from 'hooks/useLocale';
import { useTranslatedText } from 'hooks/useTranslatedText';
import { useAccount } from 'queries/accounts/useAccount';
import { PageEventFragment } from 'types/api/dato-operations.types';
import { TicketFragment } from 'types/api/tickets.types';

import LanguagePickerButton from 'components/@common/LanguagePickerButton';
import LinkUnstyled from 'components/@common/LinkUnstyled';
import TicketInquiryItem from 'components/@events/EventDetailCartContainer/TicketInquiryItem';
import Icon from 'components/@icons/Icon';
import { useTicketInfo } from 'components/@tickets/hooks';

interface Props {
  event: PageEventFragment;
  ticket: TicketFragment;
}

const EventTicketInquiryContainer = ({ event, ticket }: Props) => {
  const { account } = useAccount();
  const localize = useTranslatedText();
  const intl = useIntl();
  const { isSoldOut } = useTicketInfo(ticket.id);
  const { locale } = useLocale();

  /**
   * Add loyalty ID to external ticket URLs
   * currently this is used by jotforms
   * in pages.nofomo.com, this is not utilized
   */
  const loyaltyId = account?.ticketingLoyaltyId;
  const formUrl = appendLoyaltyIdAndLocaleToExternalTicketUrl(
    localize(ticket.externalFormUrl),
    loyaltyId,
    locale,
    { service: 'events', func: 'EventTicketInquiryContainer' },
  );

  return (
    <>
      <Container
        sx={{
          my: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <IconButton
            size="small"
            component={LinkUnstyled}
            aria-label={intl.formatMessage({ id: 'navigation.back' })}
            href={replacePathParams(Routes.EventCart, { slug: event.slug })}
            sx={{ width: 'fit-content' }}
          >
            <Icon name="ArrowLeft" rotateInArabic />
          </IconButton>

          <Typography
            variant="h6"
            component="h1"
            sx={{
              flex: '1',
            }}
          >
            {localize(ticket.name)}
          </Typography>
          <LanguagePickerButton />
        </Box>
        <TicketInquiryItem ticket={ticket} hideInquiryButton />
      </Container>
      {!isSoldOut && (
        <Box
          sx={{
            display: 'flex',
            flex: '1',
          }}
        >
          <Box
            component="iframe"
            src={formUrl || '#'}
            sx={{
              border: '0',
              flex: '1 1 auto',
            }}
          />
        </Box>
      )}
    </>
  );
};

export default EventTicketInquiryContainer;
